import { GROUP_POLICIES } from "config/groupPolicies";

/** Legacy sidenav role names equivalent to managerOrAdmin. */
export const MANAGER_OR_ADMIN_ROLE_NAMES = new Set(["Manager", "Administrator"]);

/**
 * @returns {boolean}
 */
export function readUserIsPrivAdminFromStorage() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return String(user?.priv_admin || "").trim().toUpperCase() === "Y";
  } catch {
    return false;
  }
}

/**
 * @param {{ allowedGroupIds?: number[], allowedDescriptions?: string[] }|undefined} policy
 * @returns {boolean}
 */
export function policyIncludesAdministrator(policy) {
  return (policy?.allowedDescriptions || []).some(
    (description) => String(description ?? "").trim() === "Administrator"
  );
}

/**
 * @param {unknown} value
 * @returns {number}
 */
function toPositiveInt(value) {
  const n = parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * @param {unknown} row
 * @returns {string}
 */
function rowDescription(row) {
  return String(row?.description ?? "").trim();
}

/**
 * @param {Array<{ group_id?: unknown, description?: string }>} userGroups
 * @param {{ allowedGroupIds?: number[], allowedDescriptions?: string[] }|undefined} policy
 * @returns {boolean}
 */
export function userSatisfiesPolicy(userGroups, policy) {
  if (!policy) return false;

  if (readUserIsPrivAdminFromStorage() && policyIncludesAdministrator(policy)) {
    return true;
  }

  if (!Array.isArray(userGroups)) return false;
  const idSet = new Set(
    (policy.allowedGroupIds || []).map((id) => toPositiveInt(id)).filter((n) => n > 0)
  );
  const descSet = new Set(
    (policy.allowedDescriptions || []).map((d) => String(d ?? "").trim()).filter(Boolean)
  );
  if (!idSet.size && !descSet.size) return false;

  return userGroups.some((g) => {
    const gid = toPositiveInt(g?.group_id);
    if (idSet.size && idSet.has(gid)) return true;
    if (descSet.size && descSet.has(rowDescription(g))) return true;
    return false;
  });
}

/**
 * @param {Array<{ group_id?: unknown, description?: string }>|null|undefined} userGroups
 * @param {string} policyKey
 * @returns {boolean}
 */
export function userSatisfiesPolicyKey(userGroups, policyKey) {
  const policy = GROUP_POLICIES[policyKey];
  return userSatisfiesPolicy(userGroups, policy);
}

/**
 * Whether legacy allowedRoles sidenav checks pass for priv_admin users.
 * @param {string[]} allowedRoles
 * @returns {boolean}
 */
export function privAdminSatisfiesAllowedRoles(allowedRoles) {
  if (!readUserIsPrivAdminFromStorage()) return false;
  if (!Array.isArray(allowedRoles) || allowedRoles.length < 1) return false;
  return allowedRoles.some((role) => MANAGER_OR_ADMIN_ROLE_NAMES.has(String(role ?? "").trim()));
}

/**
 * @param {Array<{ group_id?: unknown, description?: string }>|null|undefined} userGroups
 * @param {string[]} policyKeys
 * @returns {boolean} True if any listed policy passes (OR).
 */
export function userSatisfiesAnyPolicyKey(userGroups, policyKeys) {
  if (!Array.isArray(policyKeys) || policyKeys.length === 0) return false;
  return policyKeys.some((key) => userSatisfiesPolicyKey(userGroups, key));
}

/**
 * @returns {Array<{ group_id?: unknown, description?: string }>}
 */
export function readUserGroupsFromStorage() {
  try {
    const raw = localStorage.getItem("user");
    const parsed = raw ? JSON.parse(raw) : null;
    const groups = parsed?.groups;
    return Array.isArray(groups) ? groups : [];
  } catch {
    return [];
  }
}

/**
 * Warn when a policy references a description that does not exist in `sec_groups` (typo drift).
 * @param {Array<{ group_id?: unknown, description?: string }>} secGroupsCatalog
 */
export function warnUnknownPolicyDescriptions(secGroupsCatalog) {
  if (!Array.isArray(secGroupsCatalog) || secGroupsCatalog.length === 0) return;
  const known = new Set(secGroupsCatalog.map((r) => rowDescription(r)).filter(Boolean));
  Object.entries(GROUP_POLICIES).forEach(([policyKey, policy]) => {
    (policy.allowedDescriptions || []).forEach((d) => {
      const t = String(d ?? "").trim();
      if (t && !known.has(t)) {
        console.warn(
          `[groupPolicies] Policy "${policyKey}" includes description "${t}" which is not present in sec_groups catalog`
        );
      }
    });
  });
}
